import { useCompany } from "@/contexts/CompanyContext";
import { Building2, ChevronDown, Check, Plus } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export function CompanySwitcher({ collapsed }: { collapsed: boolean }) {
  const { activeCompany, getUserCompanies, switchCompany } = useCompany();
  const userCompanies = getUserCompanies();

  if (!activeCompany) return null;

  const triggerButton = (
    <button
      className={`flex items-center gap-2.5 w-full p-2 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors text-left group ${
        collapsed ? "justify-center p-2" : ""
      }`}
      aria-label="Switch active company"
    >
      <div className="w-7 h-7 rounded-md bg-blue-50 border border-blue-200/80 flex items-center justify-center shrink-0 shadow-2xs group-hover:bg-blue-100/70 transition-colors">
        <Building2 className="h-3.5 w-3.5 text-blue-600" />
      </div>
      {!collapsed && (
        <>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-900 truncate tracking-tight">{activeCompany.name}</p>
            <p className="text-[10px] text-slate-500 font-mono tracking-tight truncate">{activeCompany.gstin || "No GSTIN"}</p>
          </div>
          <ChevronDown className="h-3.5 w-3.5 text-slate-400 group-hover:text-slate-600 shrink-0 transition-colors" />
        </>
      )}
    </button>
  );

  return (
    <DropdownMenu>
      {collapsed ? (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                {triggerButton}
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p className="font-semibold text-xs">{activeCompany.name}</p>
              <p className="text-[10px] text-muted-foreground">{activeCompany.gstin || "Active Company"}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <DropdownMenuTrigger asChild>
          {triggerButton}
        </DropdownMenuTrigger>
      )}

      <DropdownMenuContent align="start" className="w-64 p-1.5 shadow-xl border border-border/80">
        <DropdownMenuLabel className="text-[11px] font-semibold text-muted-foreground px-2 py-1 uppercase tracking-wider">
          Switch Company
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="my-1" />
        {userCompanies.map((c) => (
          <DropdownMenuItem
            key={c.id}
            onClick={() => switchCompany(c.id)}
            className="flex items-center gap-2.5 px-2.5 py-2 cursor-pointer rounded-md focus:bg-accent transition-colors"
          >
            <div className="w-7 h-7 rounded-md bg-muted/60 flex items-center justify-center shrink-0">
              <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold truncate text-foreground">{c.name}</p>
              <p className="text-[10px] text-muted-foreground font-mono truncate">{c.gstin || "Standard Profile"}</p>
            </div>
            {c.id === activeCompany.id && <Check className="h-4 w-4 text-primary shrink-0" />}
            <Badge
              variant={c.status === "Active" ? "default" : "secondary"}
              className="text-[9px] h-4 px-1.5 shrink-0"
            >
              {c.status}
            </Badge>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator className="my-1" />
        <DropdownMenuItem
          className="flex items-center gap-2.5 px-2.5 py-2 cursor-pointer text-muted-foreground hover:text-foreground rounded-md"
          asChild
        >
          <a href="/dashboard/companies">
            <Plus className="h-3.5 w-3.5" />
            <span className="text-xs font-medium">Manage Companies</span>
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
